import { RLCard } from "../../../../../../components";
import Typography from "../../../../../../components/typography/Typography";
import { useLLMSummary } from "../../../../hooks/useLLMSummary";
import Loading from "../../../subheader/components/card/subtitle/Loading";
import { LLMSummary, LLMSummaryStatus } from "./@types/LLMSummary";
import LLMSummaryContent from "./components/LLMSummaryContent";
import LLMSummaryFeedback from "./components/LLMSummaryFeedback";
import LLMSummarySkeleton from "./components/LLMSummarySkeleton";
import styles from "./llm-summary.module.scss";

const countNumberOfGeneratedBlocks = (summary?: LLMSummary["summary"]) =>
  Object.values(summary?.summary ?? {}).filter(Boolean).length;

const LLMSummaryCard = () => {
  const {
    status,
    summary,
    summaryIsLoading,
    hasChanged,
    changesIsLoading,
    isError,
    refetchLLMSummary
  } = useLLMSummary(true);

  const shouldShowError =
    (status === LLMSummaryStatus.ERROR || isError) && !summaryIsLoading;

  const shouldShowFeedback =
    !summaryIsLoading && status === LLMSummaryStatus.DONE && !isError;

  const numberOfSkeletons = 6 - countNumberOfGeneratedBlocks(summary);

  return (
    <RLCard
      header={
        <RLCard.LinkHeader
          title={
            summaryIsLoading ? (
              <Loading
                text={
                  <Typography variant="h5">Generating AI Summary</Typography>
                }
              />
            ) : (
              "AI Summary"
            )
          }
          LinkComponent={
            !summaryIsLoading &&
            (changesIsLoading ? (
              <Loading
                text={<Typography variant="body2">Checking changes</Typography>}
              />
            ) : (
              <div className="flex flex-gap-2">
                {hasChanged && <span>Sample has changes: </span>}
                <span
                  onClick={refetchLLMSummary}
                  className={styles.llm_summary_refresh_button}
                >
                  Refresh
                </span>
              </div>
            ))
          }
        />
      }
      content={
        <RLCard.Content>
          <div className={styles.llm_section}>
            {shouldShowError && (
              <div>
                An error occurred while generating the AI summary. Please try
                again.
              </div>
            )}
            {!shouldShowError && (
              <LLMSummaryContent
                summary={summary}
                isProcessing={status === LLMSummaryStatus.PROCESSING}
              />
            )}
            {summaryIsLoading && (
              <div className={styles.llm_summary_skeleton_section}>
                {Array.from({ length: numberOfSkeletons - 1 }).map((_, idx) => (
                  <LLMSummarySkeleton key={`p-${idx}`} variant="paragraph" />
                ))}
                <LLMSummarySkeleton variant="list" />
              </div>
            )}
            {shouldShowFeedback && <LLMSummaryFeedback />}
          </div>
        </RLCard.Content>
      }
      data-testid="report_summary_llm_summary_card"
    />
  );
};

export default LLMSummaryCard;
